import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileText } from "lucide-react";

/**
 * Admin card: batch-link legacy dependents via CSV.
 * CSV columns: dependent_email, sponsor_email, phone(optional).
 */
export function AdminBatchLinkDependents({ api }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/dependents/batch-link", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Linked ${r.data.linked} of ${r.data.processed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch link failed");
    } finally { setBusy(false); }
  };

  const sample = "dependent_email,sponsor_email,phone\nconnecttorf5@gmail.com,trade@realtymaze.com,+91-9820000001\n";

  return (
    <div data-testid="admin-batch-link-card"
         className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6 space-y-4">
      <div className="flex items-start gap-4 border-b border-white/10 pb-4">
        <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] grid place-items-center">
          <Upload size={20}/>
        </div>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Legacy migration</div>
          <h3 className="font-serif text-xl text-[#F5F5F0] mt-1">Batch-link dependents from CSV</h3>
          <p className="font-body text-[12px] text-[#8A8A82] mt-1 max-w-2xl leading-relaxed">
            Upload a CSV with columns <code className="text-[#D8B23E]">dependent_email,sponsor_email,phone</code> (phone optional).
            Rows are validated against buyer_category linkability + sponsor Level-2 status before commit.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0])}
               data-testid="batch-link-input" className="hidden"/>
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="batch-link-choose"
                className="rounded-sm border border-white/15 hover:border-[#D8B23E]/60 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#F5F5F0] inline-flex items-center gap-2">
          <FileText size={12}/> {file ? file.name : "Choose CSV"}
        </button>
        <button type="button" onClick={submit} disabled={!file || busy} data-testid="batch-link-submit"
                className={`rounded-sm px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-2 ${
                  file && !busy ? "bg-[#D8B23E] hover:bg-[#c5a233] text-black" : "bg-white/5 text-[#8A8A82]"
                }`}>
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
          Upload & link
        </button>
        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`}
           download="vinci-dependents-sample.csv" data-testid="batch-link-sample"
           className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E]">
          Download sample
        </a>
      </div>

      {result && (
        <div className="border border-white/10 rounded-sm bg-black/40 p-4 space-y-3" data-testid="batch-link-result">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 border border-[#2FB999]/40 text-[#2FB999] font-grotesk text-[10px] uppercase tracking-[0.2em]">
              <CheckCircle2 size={12}/> Linked {result.linked}
            </span>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">
              Processed {result.processed}
            </span>
            {result.errors_count > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 border border-[#C0392B]/40 text-[#C0392B] font-grotesk text-[10px] uppercase tracking-[0.2em]">
                <AlertTriangle size={12}/> Errors {result.errors_count}
              </span>
            )}
          </div>
          {(result.errors || []).length > 0 && (
            <div className="max-h-60 overflow-y-auto border-t border-white/[0.06] pt-3">
              <table className="w-full text-[11px] font-body">
                <thead className="text-[9px] font-grotesk uppercase tracking-[0.2em] text-[#8A8A82] text-left">
                  <tr><th className="pb-1.5">Line</th><th>Dependent</th><th>Sponsor</th><th>Error</th></tr>
                </thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i} className="border-t border-white/[0.04]">
                      <td className="py-1.5 text-[#8A8A82]">{e.line}</td>
                      <td className="text-[#F5F5F0]">{e.dep_email || e.raw || "—"}</td>
                      <td className="text-[#F5F5F0]">{e.sponsor_email || "—"}</td>
                      <td className="text-[#C0392B]">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
